import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApproval, useUpdateApproval, useUpdateItem, useItem, useUserRoles, useCurrentUser } from '../hooks/useQueries';
import { formatDate } from '../utils/dateUtils';
import Toast from '../components/Popup/Toast';
import Modal from '../components/Popup/Modal';
import { formatItemRef } from '../utils/itemIdFormat';
import './approvalDetailPage.css';

// TODO: Extract to a shared utility module to keep role-id parsing consistent between client and server.
// Parses role id inputs that may come back as an array, JSON string, comma-separated string, or scalar.
const parseRoleIds = (value) => {
  if (value == null) return [];

  const toFiniteNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const dedupe = (arr) => Array.from(new Set(arr));

  // Array (numbers/strings)
  if (Array.isArray(value)) {
    return dedupe(value.map(toFiniteNumber).filter((n) => n != null));
  }

  // Scalar number
  if (typeof value === 'number') {
    const n = toFiniteNumber(value);
    return n == null ? [] : [n];
  }

  // String: JSON array, single numeric, or comma-separated list
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];

    // JSON array string
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return dedupe(parsed.map(toFiniteNumber).filter((n) => n != null));
      }
      // fall through if not an array
    } catch {
      // ignore; try other string formats below
    }

    // Single numeric string
    const single = toFiniteNumber(trimmed);
    if (single != null) return [single];

    // Comma-separated (e.g., "1,2,3")
    if (trimmed.includes(',')) {
      return dedupe(
        trimmed
          .split(',')
          .map((s) => toFiniteNumber(s.trim()))
          .filter((n) => n != null)
      );
    }

    return [];
  }

  return [];
};

const ApprovalDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [actionModal, setActionModal] = useState({ show: false, action: null });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [responseComment, setResponseComment] = useState('');

  // Fetch approval data
  const { data: approval, isLoading: approvalLoading, error: approvalError } = useApproval(id);
  const { data: item, isLoading: itemLoading } = useItem(approval?.item_id, { enabled: !!approval?.item_id });

  const { data: currentUser } = useCurrentUser();

  // Role checking function using userRoles
  const hasRole = (roleName) => {
    if (!userRoles || !Array.isArray(userRoles)) return false;
    return userRoles.some(roleObj => {
      // Check different possible role name properties
      const name = roleObj.role || roleObj.role_name || roleObj.name;
      return name && name.toLowerCase() === roleName.toLowerCase();
    });
  };

  // Mutations
  const updateApprovalMutation = useUpdateApproval();
  const updateItemMutation = useUpdateItem();

  // Role checking
  const { data: userRoles = [] } = useUserRoles();

  useEffect(() => {
    if (approvalError) {
      setToast({
        show: true,
        message: 'Failed to load approval details',
        type: 'error'
      });
    }
  }, [approvalError]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const handleCloseToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };

  const canApprove = () => {
    if (!approval || approval.status !== 'requested') {
      return false;
    }

    const ITEM_OWNER_REQUIREMENT_ID = -1;
    const ITEM_SECONDARY_OWNER_REQUIREMENT_ID = -2;
    const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

    // Admin can approve anything
    if (hasRole('admin')) {
      return true;
    }
    
    // Check if assigned to a specific user
    if (
      approval.assignment_type === 'user' ||
      approval.assigned_to_user_id ||
      approval.assigned_to_user
    ) {
      const assignedUserId = approval.assigned_to_user_id || approval.assigned_to_user;

      // Allow the assigned user; keep existing endorser/admin override behavior.
      if (assignedUserId != null && currentUser?.id != null) {
        return (
          Number(currentUser.id) === Number(assignedUserId) ||
          hasRole('endorser') ||
          hasRole('admin')
        );
      }

      return hasRole('endorser') || hasRole('admin');
    }

    // Role-based assignment: approvals can be assigned to multiple roles.
    // If the current user has ANY of the assigned roles, allow approval.
    const requiredRoleIds = Array.isArray(approval.assigned_to_role_ids) && approval.assigned_to_role_ids.length > 0
      ? approval.assigned_to_role_ids
      : (approval.assigned_to_roles ? parseRoleIds(approval.assigned_to_roles) : []);

    // Back-compat: older single-role field.
    const fallbackSingleRoleId = Number(approval.assigned_to_role);
    const effectiveRequiredRoleIds = requiredRoleIds.length > 0
      ? requiredRoleIds
      : (Number.isFinite(fallbackSingleRoleId) ? [fallbackSingleRoleId] : []);

    if (effectiveRequiredRoleIds.length === 0) {
      return false;
    }

    // New model: when specific approvers were selected, only they (or the item
    // owners for special requirements) may respond — not every role member.
    const selectedUserIds = Array.isArray(approval.assigned_to_user_ids)
      ? approval.assigned_to_user_ids.map(Number).filter((n) => Number.isFinite(n))
      : [];
    const hasSelectedApprovers = selectedUserIds.length > 0;

    if (hasSelectedApprovers) {
      if (currentUser?.id != null && selectedUserIds.includes(Number(currentUser.id))) {
        return true;
      }
      // Not selected — fall through to the owner-based checks only (no role intersection).
    } else {
      const userRoleIds = (userRoles || [])
        .map((r) => Number(r.role_id))
        .filter((n) => Number.isFinite(n));

      // Legacy: any member of an assigned role may respond.
      const hasMatchingRole = effectiveRequiredRoleIds.some((roleId) => userRoleIds.includes(Number(roleId)));
      if (hasMatchingRole) return true;
    }

    // Owner-based requirement targets
    const currentUserEmail = normalizeEmail(currentUser?.email);
    if (!currentUserEmail) return false;

    const matchesOwner = effectiveRequiredRoleIds.includes(ITEM_OWNER_REQUIREMENT_ID)
      && currentUserEmail === normalizeEmail(item?.primary_owner);
    if (matchesOwner) return true;

    const matchesSecondaryOwner = effectiveRequiredRoleIds.includes(ITEM_SECONDARY_OWNER_REQUIREMENT_ID)
      && currentUserEmail === normalizeEmail(item?.secondary_owner);
    return matchesSecondaryOwner;
  };

  const handleCancelAction = () => {
    setActionModal({ show: false, action: null });
  };

  const handleConfirmAction = async () => {
    const action = actionModal.action;
    try {
      const itemApprovalStatus = action === 'approve' ? 'approved' : 'denied';

      // First update the approval record (reuse the inline response comment).
      await updateApprovalMutation.mutateAsync({
        id: approval.id,
        status: itemApprovalStatus,
        comments: responseComment.trim()
      });

      // Then update the item approval_status
      await updateItemMutation.mutateAsync({
        itemId: approval.item_id,
        updateData: { approval_status: itemApprovalStatus },
        fieldId: 'approval_status'
      });

      showToast(
        `Approval ${action === 'approve' ? 'approved' : 'denied'} successfully`,
        'success'
      );

      setActionModal({ show: false, action: null });
      setResponseComment('');

      // Navigate back to approvals page after a short delay
      setTimeout(() => {
        navigate('/approvals');
      }, 800);

    } catch (error) {
      console.error('Error processing approval action:', error);
      showToast('Failed to process approval action', 'error');
    }
  };

  const handleBackToApprovals = () => {
    navigate('/approvals');
  };

  const handleViewItem = () => {
    if (approval?.item_id) {
      navigate(`/items/${approval.item_id}?source=approval&sourceApprovalId=${approval.id}`);
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'badge bg-success';
      case 'denied':
        return 'badge bg-danger';
      case 'requested':
      case 'pending':
        return 'badge bg-warning';
      default:
        return 'badge bg-secondary';
    }
  };

  if (approvalLoading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!approval) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger">
          <h4>Approval Not Found</h4>
          <p>The approval you're looking for could not be found.</p>
          <button className="btn btn-primary" onClick={handleBackToApprovals}>
            Back to Approvals
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="item-detail-page approval-detail-page">
      <div className="item-detail-container">
        {/* Header */}
        <div className="detail-header">
          <div className="header-left">
            <button 
              className="back-btn"
              onClick={handleBackToApprovals}
              title="Back to Approvals"
            >
              <i className="bi bi-arrow-left"></i>
            </button>
            <div className="header-info">
              <h1>Approval #{approval.id}</h1>
              <span className={getStatusBadge(approval.status)}>
                {approval.status?.charAt(0).toUpperCase() + approval.status?.slice(1)}
              </span>
            </div>
          </div>
          
          <div className="header-actions">
          </div>
        </div>

        {/* Content */}
        <div className="detail-content">
          <div className="detail-sections-row">
            {/* Approval Information */}
            <div className="detail-section">
              <h4>
                <i className="bi bi-clipboard-check me-2"></i>
                Approval Information
              </h4>
              <div className="detail-grid">
                <div className="detail-field">
                  <label>Approval ID</label>
                  <div className="field-value">#{approval.id}</div>
                </div>
                <div className="detail-field">
                  <label>Status</label>
                  <div className="field-value">
                    <span className={getStatusBadge(approval.status)}>
                      {approval.status?.charAt(0).toUpperCase() + approval.status?.slice(1)}
                    </span>
                  </div>
                </div>
                <div className="detail-field">
                  <label>Stage</label>
                  <div className="field-value">{approval.stage_name || 'N/A'}</div>
                </div>
                <div className="detail-field">
                  <label>Assigned To</label>
                  <div className="field-value">
                    {approval.assignment_type === 'user' || approval.assigned_to_user ? (
                      <span>
                        <i className="bi bi-person text-primary me-1"></i>
                        {approval.assigned_to_user_name || approval.assigned_to_user || 'Unknown User'}
                      </span>
                    ) : (
                      <span>
                        <i className="bi bi-shield text-warning me-1"></i>
                        {approval.assigned_to_role_name || approval.assignment_display || 'Unknown Assignment'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="detail-field">
                  <label>Requested By</label>
                  <div className="field-value">{approval.requested_by_name || 'N/A'}</div>
                </div>
                <div className="detail-field">
                  <label>Created</label>
                  <div className="field-value">{formatDate(approval.created_at)}</div>
                </div>
                {approval.comments && (
                  <div className="detail-field full-width">
                    <label>Comments</label>
                    <div className="field-value">
                      <div style={{
                        backgroundColor: '#f8f9fa',
                        padding: '12px',
                        borderRadius: '6px',
                        border: '1px solid #e9ecef'
                      }}>
                        {approval.comments}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Item Information */}
            <div className="detail-section">
              <h4>
                <i className="bi bi-file-text me-2"></i>
                Related Item
                {approval.item_id && (
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={handleViewItem}
                    style={{ 
                      float: 'right',
                      fontSize: '12px',
                      padding: '4px 8px',
                      marginTop: '-4px'
                    }}
                  >
                    <i className="bi bi-eye me-1"></i>
                    View Item
                  </button>
                )}
              </h4>
              <div className="detail-grid">
                {itemLoading ? (
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <div className="spinner-border spinner-border-sm" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : item ? (
                  <>
                    <div className="detail-field">
                      <label>Item ID</label>
                      <div className="field-value">{formatItemRef(item.id)}</div>
                    </div>
                    <div className="detail-field">
                      <label>Title</label>
                      <div className="field-value">{item.title || 'No title'}</div>
                    </div>
                    <div className="detail-field">
                      <label>Category</label>
                      <div className="field-value">{item.category || 'N/A'}</div>
                    </div>
                    <div className="detail-field">
                      <label>Current Stage</label>
                      <div className="field-value">{item.stage_name || 'N/A'}</div>
                    </div>
                    <div className="detail-field">
                      <label>Status</label>
                      <div className="field-value">
                        <span className={`badge ${item.status === 'open' ? 'bg-success' : 'bg-secondary'}`}>
                          {item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
                        </span>
                      </div>
                    </div>
                    <div className="detail-field">
                      <label>Approval Status</label>
                      <div className="field-value">
                        <span className={getStatusBadge(item.approval_status)}>
                          {item.approval_status?.charAt(0).toUpperCase() + item.approval_status?.slice(1) || 'None'}
                        </span>
                      </div>
                    </div>
                    {item.description && (
                      <div className="detail-field full-width">
                        <label>Description</label>
                        <div className="field-value">
                          <div style={{
                            backgroundColor: '#f8f9fa',
                            padding: '12px',
                            borderRadius: '6px',
                            border: '1px solid #e9ecef',
                            maxHeight: '150px',
                            overflowY: 'auto'
                          }}>
                            {item.description}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeaa7',
                    borderRadius: '6px'
                  }}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    Item information could not be loaded.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Approval Response Section */}
          {approval.status === 'requested' && (
            <div className="detail-section">
              <h4>
                <i className="bi bi-check-square me-2"></i>
                Respond to Approval Request
                {!canApprove() && (
                  <small className="text-muted ms-2">(You are not authorized to approve this request)</small>
                )}
              </h4>
              <div className="detail-grid one-column">
                <div className="detail-field">
                  <label>Actions</label>
                  <div className="field-value">
                    {canApprove() ? (
                      <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                        <button
                          className="btn btn-success"
                          onClick={() => setActionModal({ show: true, action: 'approve' })}
                          disabled={updateApprovalMutation.isLoading}
                          style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 18px',
                            fontWeight: '500'
                          }}
                        >
                          <i className="bi bi-check-circle"></i>
                          {updateApprovalMutation.isLoading ? 'Processing...' : 'Approve'}
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() => setActionModal({ show: true, action: 'deny' })}
                          disabled={updateApprovalMutation.isLoading}
                          style={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 18px',
                            fontWeight: '500'
                          }}
                        >
                          <i className="bi bi-x-circle"></i>
                          {updateApprovalMutation.isLoading ? 'Processing...' : 'Deny'}
                        </button>
                      </div>
                    ) : (
                      <div style={{ 
                        padding: '12px', 
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        border: '1px solid #e9ecef',
                        color: '#6c757d'
                      }}>
                        <i className="bi bi-info-circle me-2"></i>
                        You do not have permission to approve this request. 
                        {(approval.assignment_type === 'user' || approval.assigned_to_user) && (
                          <span> This approval is assigned to {approval.assigned_to_user_name || approval.assigned_to_user || 'another user'}.</span>
                        )}
                        {approval.assignment_type === 'role' && approval.assigned_to_role && (
                          <span> This approval requires <strong>{approval.assigned_to_role_name || approval.assignment_display || 'Unknown Assignment'}</strong>.</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="detail-field">
                  <label>Your Response Comment (optional)</label>
                  <div className="field-value">
                    <textarea
                      value={responseComment}
                      onChange={(e) => setResponseComment(e.target.value)}
                      className="form-control"
                      rows={3}
                      placeholder="Optional: add context for your decision..."
                      disabled={!canApprove()}
                      style={{
                        width: '100%',
                        borderRadius: '6px',
                        border: '1px solid #ced4da',
                        padding: '10px'
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action History (if any) */}
          {approval.status !== 'requested' && (
            <div className="detail-section">
              <h4>
                <i className="bi bi-clock-history me-2"></i>
                Action History
              </h4>
              <div className="detail-grid one-column">
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'start',
                  padding: '16px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '6px',
                  border: '1px solid #e9ecef'
                }}>
                  <div className={`me-3 ${approval.status === 'approved' ? 'text-success' : 'text-danger'}`}>
                    <i className={`bi ${approval.status === 'approved' ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`} style={{ fontSize: '24px' }}></i>
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                      {approval.status === 'approved' ? 'Approved' : 'Denied'}
                      {approval.updated_at && (
                        <span className="text-muted ms-2" style={{ fontWeight: 'normal' }}>
                          on {formatDate(approval.updated_at)}
                        </span>
                      )}
                    </div>
                    {approval.comments && (
                      <div className="text-muted">
                        "{approval.comments}"
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Modal (confirm approve/deny) */}
      {(() => {
        const isApprove = actionModal.action === 'approve';
        const actionLabel = isApprove ? 'approve' : 'deny';
        const commentLabel = isApprove ? 'Approval Comment' : 'Denial Comment';
        return (
          <Modal
            show={actionModal.show}
            title={isApprove ? 'Approve Approval Request' : 'Deny Approval Request'}
            onCancel={handleCancelAction}
            onConfirm={handleConfirmAction}
            confirmText={updateApprovalMutation.isLoading
              ? (isApprove ? 'Approving...' : 'Denying...')
              : (isApprove ? 'Approve' : 'Deny')}
            cancelText="Cancel"
            confirmButtonClass={isApprove ? 'btn-success' : 'btn-danger'}
            confirmDisabled={updateApprovalMutation.isLoading}
            content={
              <div className="approval-action-form">
                <div className={`alert ${isApprove ? 'alert-info' : 'alert-warning'}`}>
                  <i className={`bi ${isApprove ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-2`}></i>
                  Are you sure you want to <strong>{actionLabel}</strong> this approval request?
                  This action cannot be undone.
                </div>

                {responseComment.trim() ? (
                  <div className="form-group">
                    <label className="form-label">
                      <strong>{commentLabel}</strong>
                    </label>
                    <div
                      className="form-control"
                      style={{ minHeight: '60px', whiteSpace: 'pre-wrap', backgroundColor: '#f8f9fa' }}
                    >
                      {responseComment.trim()}
                    </div>
                  </div>
                ) : (
                  <p className="text-muted mb-0">
                    No comment was entered. You can cancel to add one in the response comment field.
                  </p>
                )}
              </div>
            }
          />
        );
      })()}

      {/* Toast */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={handleCloseToast}
      />
    </div>
  );
};

export default ApprovalDetailPage;